import { ChevronRight, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { formatCents, formatMonth, monthOf, todayIso } from "@budgeter/shared";
import { useBudget, useSnapshot } from "../../data/StoreContext";
import { accountsWithBalances, type AccountWithBalance } from "../../data/selectors";
import { PageHeader } from "../../layout/PageHeader";
import { SyncIndicator } from "../../layout/SyncIndicator";
import { BalancePill, cn, Money } from "../../ui";
import { homeGroups } from "./homeGroups";

const sectionHeading =
  "px-1 pt-3 pb-1.5 text-[12px] font-bold tracking-[.5px] text-muted uppercase";
const card = "overflow-hidden rounded-card border border-line bg-surface";
const chip = "min-h-11 rounded-full px-3.5 text-[14px] font-bold";

/** Phone Home (SPEC §11.3): category balances, account balances, sync status. No budgeting. */
export function HomePage() {
  const month = monthOf(todayIso());
  const s = useSnapshot();
  const budget = useBudget();
  const [query, setQuery] = useState("");
  const [overspentOnly, setOverspentOnly] = useState(false);
  const { groups, overspentCount } = useMemo(
    () => homeGroups(s, budget, month, { query, overspentOnly }),
    [s, budget, month, query, overspentOnly],
  );
  const atb = budget.month(month).availableToBudget;
  const accounts = accountsWithBalances(s);
  const monthName = formatMonth(month).split(" ")[0] ?? month;

  return (
    <div className="flex flex-col">
      <PageHeader title={monthName} actions={<SyncIndicator className="text-[12.5px]" />} />
      <div className="flex flex-col gap-3 px-4 pb-2.5">
        <div className="flex items-center justify-between rounded-2xl border border-line bg-surface px-3.5 py-3">
          <span className="text-[14px] text-muted">Available to budget</span>
          <span
            className={cn(
              "num rounded-xl px-3 py-1 text-[22px] font-extrabold",
              atb < 0 ? "bg-neg-bg text-neg" : "bg-pos-bg text-pos",
            )}
          >
            {formatCents(atb)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-pressed={!overspentOnly}
            onClick={() => setOverspentOnly(false)}
            className={cn(chip, overspentOnly ? "bg-chip text-ink" : "bg-ink text-bg")}
          >
            All
          </button>
          <button
            type="button"
            aria-pressed={overspentOnly}
            onClick={() => setOverspentOnly(true)}
            className={cn(chip, overspentOnly ? "bg-neg text-white" : "bg-neg-bg text-neg")}
          >
            Overspent · {overspentCount}
          </button>
          <label className="flex min-h-11 min-w-0 flex-1 items-center gap-1.5 rounded-full border border-line bg-surface px-3">
            <Search size={14} className="shrink-0 text-muted" aria-hidden />
            <span className="sr-only">Search categories</span>
            <input
              type="search"
              placeholder="Search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full min-w-0 border-0 bg-transparent text-[16px] text-ink outline-none"
            />
          </label>
        </div>
      </div>

      <div className="px-4 pb-3">
        {groups.map((g) => (
          <section key={g.id} aria-label={g.name}>
            <h2 className={sectionHeading}>{g.name}</h2>
            <ul className={cn(card, "m-0 list-none p-0")}>
              {g.rows.map((r) => (
                <li key={r.key} className="border-b border-line-soft last:border-b-0">
                  <Link
                    to={`/category/${r.key}/${month}`}
                    className="flex min-h-[46px] items-center justify-between gap-2.5 px-3.5 text-[15px] text-ink no-underline"
                  >
                    <span className="min-w-0 truncate">{r.name}</span>
                    <span className="flex shrink-0 items-center gap-1.5">
                      <BalancePill cents={r.available} tone={r.tone} />
                      <ChevronRight size={14} className="text-faint" aria-hidden />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
        {groups.length === 0 && (
          <p className="m-0 px-1 py-4 text-[14px] text-muted">
            {overspentOnly
              ? "Nothing is overspent this month."
              : query
                ? `No category matches "${query}".`
                : "No categories yet. Set them up in the full app."}
          </p>
        )}
        {overspentOnly && overspentCount > 0 && (
          <p className="m-0 px-1 py-3 text-[13px] leading-[1.45] text-muted">
            Cover overspending in the full app when you next budget. Categories set to carry a
            negative balance forward aren't counted as overspent.
          </p>
        )}

        {!overspentOnly && !query && (
          <>
            <AccountGroup title="On budget accounts" accounts={accounts.onBudget} />
            <AccountGroup title="Off budget accounts" accounts={accounts.offBudget} />
          </>
        )}
      </div>
    </div>
  );
}

function AccountGroup({ title, accounts }: { title: string; accounts: AccountWithBalance[] }) {
  if (accounts.length === 0) {
    return null;
  }
  return (
    <section aria-label={title}>
      <h2 className={sectionHeading}>{title}</h2>
      <ul className={cn(card, "m-0 list-none p-0")}>
        {accounts.map((a) => (
          <li key={a.id} className="border-b border-line-soft last:border-b-0">
            <Link
              to={`/accounts/${a.id}`}
              className="flex min-h-[46px] items-center justify-between gap-2.5 px-3.5 text-[15px] text-ink no-underline"
            >
              <span className="min-w-0 truncate">{a.name}</span>
              <Money cents={a.balance.working} tone="negRed" className="font-bold" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
